<template>
  <div class="ai-assistant" :class="{ 'ai-assistant--open': open }">
    <transition name="assistant-panel">
      <section v-if="open" class="ai-assistant__panel" role="dialog" :aria-label="assistantTitle">
        <header class="ai-assistant__header">
          <span class="ai-assistant__avatar">
            <img v-if="companyLogo" :src="companyLogo" alt="" @error="companyLogo = null" />
            <vue-feather v-else type="message-circle" />
          </span>
          <div>
            <strong class="ai-assistant__title">{{ assistantTitle }}</strong>
            <small><i></i>{{ $t('aiAssistant.online') }}</small>
          </div>
          <button type="button" :aria-label="$t('aiAssistant.close')" @click="open = false">
            <vue-feather type="x" />
          </button>
        </header>

        <div ref="body" class="ai-assistant__body">
          <div v-if="!messages.length" class="ai-assistant__welcome">
            <span><vue-feather type="cpu" /></span>
            <h4>{{ $t('aiAssistant.welcome') }}</h4>
            <p>{{ $t('aiAssistant.intro') }}</p>
          </div>
          <div v-if="!messages.length" class="ai-assistant__suggestions">
            <button v-for="suggestion in suggestions" :key="suggestion.key" type="button" @click="selectSuggestion(suggestion.text)">
              <vue-feather :type="suggestion.icon" />
              <span>{{ suggestion.text }}</span>
            </button>
          </div>
          <div v-else class="ai-assistant__messages" aria-live="polite">
            <div v-for="(item, index) in messages" :key="index" class="ai-assistant__message" :class="`ai-assistant__message--${item.role}`">
              <span v-if="item.role === 'assistant'"><vue-feather type="cpu" /></span>
              <p>{{ item.content }}</p>
            </div>
            <div v-if="sending" class="ai-assistant__message ai-assistant__message--assistant">
              <span><vue-feather type="cpu" /></span>
              <p class="ai-assistant__typing"><i></i><i></i><i></i></p>
            </div>
          </div>
        </div>

        <form class="ai-assistant__composer" @submit.prevent="submit">
          <textarea v-model.trim="message" rows="1" :disabled="sending" :placeholder="$t('aiAssistant.placeholder')" @keydown.enter.exact.prevent="submit"></textarea>
          <button type="submit" :disabled="!message || sending" :aria-label="$t('aiAssistant.send')">
            <vue-feather type="send" />
          </button>
        </form>
        <p class="ai-assistant__notice">{{ $t('aiAssistant.notice') }}</p>
      </section>
    </transition>

    <button class="ai-assistant__trigger" type="button" :aria-label="$t('aiAssistant.open')" :aria-expanded="open" @click="open = !open">
      <vue-feather :type="open ? 'x' : 'message-circle'" />
      <span v-if="!open" class="ai-assistant__pulse"></span>
    </button>
  </div>
</template>

<script>
import axios from "@/utils/axios";
import { getCompanyData, getCompanyLogoDataUrl } from "@/services/companyService";

export default {
  name: "AiAssistantWidget",
  data() {
    return { open: false, message: "", messages: [], sending: false, companyLogo: null, companyName: "", brandingListener: null };
  },
  computed: {
    assistantTitle() {
      if (!this.companyName) return this.$t("aiAssistant.title");
      const locale = this.$i18n.locale;
      if (locale === "ar") return `مساعد ${this.companyName} الذكي`;
      if (locale === "fr") return `Assistant IA ${this.companyName}`;
      return `${this.companyName} AI Assistant`;
    },
    suggestions() {
      return [
        { key: "quote", icon: "file-text", text: this.$t("aiAssistant.quoteSuggestion") },
        { key: "invoice", icon: "search", text: this.$t("aiAssistant.invoiceSuggestion") },
        { key: "stock", icon: "package", text: this.$t("aiAssistant.stockSuggestion") },
      ];
    },
  },
  watch: {
    "$route.fullPath"() {
      this.open = false;
    },
  },
  mounted() {
    this.loadCompanyLogo();
    this.brandingListener = () => this.loadCompanyLogo();
    window.addEventListener("company-branding-updated", this.brandingListener);
  },
  beforeUnmount() {
    if (this.brandingListener) {
      window.removeEventListener("company-branding-updated", this.brandingListener);
    }
  },
  methods: {
    async loadCompanyLogo() {
      try {
        const [logo, response] = await Promise.all([
          getCompanyLogoDataUrl("logo_small"),
          getCompanyData(),
        ]);
        this.companyLogo = logo;
        const company = response?.data || {};
        this.companyName = String(company.societe_nom || company.nom || "").trim();
      } catch (_) {
        this.companyLogo = null;
        this.companyName = "";
      }
    },
    selectSuggestion(text) {
      this.message = text;
      this.$nextTick(() => this.$el.querySelector("textarea")?.focus());
    },
    async submit() {
      if (!this.message || this.sending) return;
      const content = this.message;
      if (["clear", "/clear", "مسح", "امسح", "effacer"].includes(content.toLocaleLowerCase().trim())) {
        this.messages = [];
        this.message = "";
        return;
      }
      const history = this.messages.map(({ role, content: text }) => ({ role, content: text }));
      this.messages.push({ role: "user", content });
      this.message = "";
      this.sending = true;
      this.scrollToBottom();
      try {
        const { data } = await axios.post("/ai/chat", { message: content, locale: this.$i18n.locale, history });
        this.messages.push({ role: "assistant", content: data?.reply || this.$t("aiAssistant.emptyReply") });
      } catch (error) {
        const notConfigured = ["openai_not_configured", "ai_not_configured"].includes(error?.response?.data?.code);
        this.messages.push({
          role: "assistant",
          content: notConfigured ? this.$t("aiAssistant.notConfigured") : (error?.response?.data?.message || this.$t("aiAssistant.error")),
        });
      } finally {
        this.sending = false;
        this.scrollToBottom();
      }
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const body = this.$refs.body;
        if (body) body.scrollTop = body.scrollHeight;
      });
    },
  },
};
</script>

<style scoped>
.ai-assistant{position:fixed;inset-inline-end:24px;bottom:24px;z-index:1055;display:flex;flex-direction:column;align-items:flex-end;gap:14px;font-family:inherit}.ai-assistant__trigger{position:relative;width:58px;height:58px;border:0;border-radius:18px;background:linear-gradient(145deg,#0b365a,#176f9e);color:#fff;display:grid;place-items:center;box-shadow:0 12px 30px rgba(9,44,76,.32);transition:transform .2s ease,box-shadow .2s ease}.ai-assistant__trigger:hover{transform:translateY(-2px);box-shadow:0 16px 34px rgba(9,44,76,.38)}.ai-assistant__trigger svg{width:25px;height:25px}.ai-assistant__pulse{position:absolute;inset-inline-end:-2px;top:-2px;width:13px;height:13px;border:3px solid #fff;border-radius:50%;background:#ff9f43}.ai-assistant__panel{width:min(380px,calc(100vw - 28px));height:min(590px,calc(100vh - 110px));background:#fff;border:1px solid #e2e8f0;border-radius:20px;box-shadow:0 24px 65px rgba(15,35,55,.24);overflow:hidden;display:flex;flex-direction:column}.ai-assistant__header{padding:16px 18px;color:#fff;background:linear-gradient(125deg,#092c4c,#176f9e);display:flex;align-items:center;gap:11px}.ai-assistant__avatar{width:42px;height:42px;padding:4px;border-radius:13px;background:#fff;display:grid;place-items:center;overflow:hidden}.ai-assistant__avatar img{width:100%;height:100%;display:block;object-fit:contain;border-radius:9px}.ai-assistant__avatar svg{width:21px;color:#176f9e}.ai-assistant__header>div{flex:1}.ai-assistant__header strong,.ai-assistant__header small{display:block}.ai-assistant__header strong{font-size:1rem}.ai-assistant__title{display:block!important}.ai-assistant__header small{font-size:.72rem;color:#d5e8f4;margin-top:3px}.ai-assistant__header small i{display:inline-block;width:7px;height:7px;border-radius:50%;background:#3dd598;margin-inline-end:5px}.ai-assistant__header button{width:34px;height:34px;border:0;border-radius:10px;background:rgba(255,255,255,.1);color:#fff;display:grid;place-items:center}.ai-assistant__header button svg{width:18px}.ai-assistant__body{flex:1;overflow:auto;padding:24px 18px;background:#f8fafc}.ai-assistant__welcome{text-align:center;padding:12px 8px 22px}.ai-assistant__welcome>span{width:52px;height:52px;margin:auto auto 12px;border-radius:16px;background:#e7f2fb;color:#176f9e;display:grid;place-items:center}.ai-assistant__welcome>span svg{width:23px}.ai-assistant__welcome h4{font-size:1rem;color:#12263a;margin:0 0 7px}.ai-assistant__welcome p{font-size:.82rem;line-height:1.65;color:#64748b;margin:0}.ai-assistant__suggestions{display:grid;gap:9px}.ai-assistant__suggestions button{width:100%;min-height:52px;border:1px solid #e1e8ef;border-radius:13px;background:#fff;color:#334155;padding:10px 12px;display:flex;align-items:center;gap:10px;text-align:start;transition:.18s ease}.ai-assistant__suggestions button:hover{border-color:#ffb36d;background:#fffaf5;transform:translateY(-1px)}.ai-assistant__suggestions svg{width:18px;color:#ff9f43;flex:none}.ai-assistant__suggestions span{font-size:.8rem;line-height:1.4}.ai-assistant__messages{display:flex;flex-direction:column;gap:12px}.ai-assistant__message{display:flex;align-items:flex-end;gap:8px}.ai-assistant__message>span{width:29px;height:29px;border-radius:9px;background:#e7f2fb;color:#176f9e;display:grid;place-items:center;flex:none}.ai-assistant__message>span svg{width:14px}.ai-assistant__message p{max-width:82%;white-space:pre-wrap;margin:0;padding:10px 12px;border-radius:14px;font-size:.8rem;line-height:1.55;background:#fff;border:1px solid #e2e8f0;color:#334155}.ai-assistant__message--user{justify-content:flex-end}.ai-assistant__message--user p{background:#0b365a;border-color:#0b365a;color:#fff;border-end-end-radius:4px}.ai-assistant__message--assistant p{border-end-start-radius:4px}.ai-assistant__typing{display:flex;gap:4px!important;padding:13px 14px!important}.ai-assistant__typing i{width:6px;height:6px;border-radius:50%;background:#94a3b8;animation:assistant-dot 1s infinite alternate}.ai-assistant__typing i:nth-child(2){animation-delay:.2s}.ai-assistant__typing i:nth-child(3){animation-delay:.4s}@keyframes assistant-dot{to{opacity:.25;transform:translateY(-2px)}}.ai-assistant__composer{padding:12px 13px 7px;border-top:1px solid #e8edf2;background:#fff;display:flex;align-items:flex-end;gap:8px}.ai-assistant__composer textarea{flex:1;resize:none;max-height:96px;min-height:43px;border:1px solid #dbe3eb;border-radius:13px;padding:11px 13px;outline:none;font-size:.82rem;line-height:1.5}.ai-assistant__composer textarea:focus{border-color:#ff9f43;box-shadow:0 0 0 3px rgba(255,159,67,.1)}.ai-assistant__composer button{width:43px;height:43px;flex:none;border:0;border-radius:13px;background:#ff9f43;color:#fff;display:grid;place-items:center}.ai-assistant__composer button:disabled{opacity:.45}.ai-assistant__composer svg{width:18px}.ai-assistant__notice{text-align:center;color:#94a3b8;background:#fff;font-size:.65rem;margin:0;padding:2px 12px 10px}.assistant-panel-enter-active,.assistant-panel-leave-active{transition:opacity .2s ease,transform .2s ease}.assistant-panel-enter-from,.assistant-panel-leave-to{opacity:0;transform:translateY(12px) scale(.97)}
html[dir="rtl"] .ai-assistant{inset-inline-end:24px;align-items:flex-end}.ai-assistant__composer button svg{transform:rotate(0)}html[dir="rtl"] .ai-assistant__composer button svg{transform:rotate(180deg)}
@media(max-width:575px){.ai-assistant{inset-inline-end:14px;bottom:14px}.ai-assistant__trigger{width:54px;height:54px;border-radius:17px}.ai-assistant__panel{width:calc(100vw - 28px);height:min(560px,calc(100dvh - 92px));border-radius:18px}}
</style>
